import React, { useState } from 'react';
import { X, Loader2, Car, AlertTriangle, Check, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

/**
 * Work out a worker's travel for a day from the addresses already on file.
 *
 * Preview first, apply second, and never in one step. This writes billable kilometres, so
 * whoever presses the button sees the legs and the numbers before they become an invoice
 * line — including the addresses it could not resolve, because a total that quietly omits
 * the legs it failed on looks complete and is not.
 */
export default function AutoTravelModal({ staff = [], onClose, onApplied }) {
  const [staffId, setStaffId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);

  const preview = async () => {
    if (!staffId || !date) return toast.error('Choose a staff member and a date');
    setBusy(true); setResult(null);
    try {
      const { data } = await api.get('/timesheets/auto-travel', { params: { staff_id: staffId, date } });
      setResult(data);
      if (!data.legs?.length && !data.unresolved?.length) toast('No shifts that day');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not work that out');
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post('/timesheets/auto-travel', { staff_id: staffId, date });
      toast.success(`${data.written} travel leg(s) recorded — ${data.totalKm} km`);
      onApplied && onApplied();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not record it');
    } finally { setApplying(false); }
  };

  const person = staff.find(s => s.id === staffId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="travel-title" className="bg-white w-full max-w-2xl rounded-2xl shadow-modal max-h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()}>

        <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2">
              <Car size={18} className="text-primary-600" />
              <h3 id="travel-title" className="font-bold text-slate-900">Calculate travel</h3>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              Home to the first participant, between participants, and back — measured from the
              addresses on file.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-2 -m-1 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="travel-staff" className="text-xs font-semibold text-slate-600">Staff member</label>
              <select id="travel-staff" value={staffId} onChange={e => { setStaffId(e.target.value); setResult(null); }}
                className="input-field w-full text-sm mt-1">
                <option value="">Select…</option>
                {staff.map(s => (
                  <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="travel-date" className="text-xs font-semibold text-slate-600">Date</label>
              <input id="travel-date" type="date" value={date} onChange={e => { setDate(e.target.value); setResult(null); }}
                className="input-field w-full text-sm mt-1" />
            </div>
          </div>

          <button onClick={preview} disabled={busy}
            className="btn-secondary text-sm w-full flex items-center justify-center gap-2">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <MapPin size={15} />}
            Work out travel
          </button>

          {result && (
            <>
              {result.legs?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-slate-500">
                      {result.legs.length} leg{result.legs.length === 1 ? '' : 's'}
                      {person && ` for ${person.first_name}`}
                    </span>
                    <span className="text-sm font-bold text-slate-800 tabular-nums">{result.totalKm} km</span>
                  </div>
                  <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
                    {result.legs.map((l, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                        <span className="text-[11px] font-semibold text-slate-500 w-16 shrink-0">
                          {String(l.kind || '').replace('_', ' ')}
                        </span>
                        <span className="flex-1 min-w-0 truncate text-slate-700">{l.from} → {l.to}</span>
                        <span className="text-[11px] text-slate-500 tabular-nums shrink-0">{l.straight_km} straight</span>
                        <span className="font-semibold tabular-nums text-slate-800 shrink-0 w-16 text-right">{l.km} km</span>
                      </div>
                    ))}
                  </div>
                  {/* Say what the number is, so nobody reads it as an odometer reading. */}
                  <p className="text-[11px] text-slate-500 mt-1.5">
                    Straight-line distance × {result.cfg?.roadFactor} to approximate roads. Home legs:{' '}
                    {result.cfg?.homeLegs === 'first_last' ? 'first and last shift only'
                      : result.cfg?.homeLegs === 'every_shift' ? 'every shift' : 'not charged'}.
                    Anyone can correct a leg afterwards.
                  </p>
                </div>
              )}

              {result.unresolved?.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3">
                  <p className="text-xs font-bold text-amber-800 flex items-center gap-1.5 mb-1">
                    <AlertTriangle size={13} /> {result.unresolved.length} could not be worked out
                  </p>
                  <ul className="text-[12px] text-amber-800/90 space-y-0.5 list-disc list-inside">
                    {result.unresolved.slice(0, 6).map((u, i) => <li key={i}>{u.why}</li>)}
                  </ul>
                  <p className="text-[11px] text-amber-700 mt-1.5">
                    These legs are left out of the total rather than counted as zero — add the missing
                    address, or enter those kilometres by hand.
                  </p>
                </div>
              )}

              {!result.legs?.length && !result.unresolved?.length && (
                <p className="text-sm text-slate-500 text-center py-4">No shifts on that day.</p>
              )}
            </>
          )}
        </div>

        <div className="p-5 border-t border-slate-100 flex gap-2">
          <button onClick={onClose} className="btn-secondary text-sm flex-1">Cancel</button>
          <button onClick={apply} disabled={applying || !result?.legs?.length}
            className="btn-primary text-sm flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
            {applying ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
            Record {result?.legs?.length ? `${result.totalKm} km` : 'travel'}
          </button>
        </div>
      </div>
    </div>
  );
}
